import { computed, unref } from 'vue';

import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';
import { useTiktokStore } from '@/stores/tiktok';

export function useTikTokAdsAccountDetails() {
  const tiktokStore = useTiktokStore();
  const identityStore = useIdentityStore();

  const adAccountsList = computed(() =>
    tiktokStore.adAccountsList.map((account) => ({
      ...account,
      sourceAdAccountId: account.sourceAdvertiserId,
    })),
  );

  const adCampaigns = computed(() => tiktokStore.adCampaigns);

  const adCampaignsList = computed(() => {
    if (!tiktokStore.adCampaigns) return [];
    return Object.values(tiktokStore.adCampaigns);
  });

  const adAccountToCurrency = computed(() => tiktokStore.tiktokAdsAccountToCurrency);

  const adAccountsLoading = computed(() => tiktokStore.pending.adAccounts);
  const adCampaignsLoading = computed(() => tiktokStore.pending.adCampaigns);
  const adAccountsError = computed(() => tiktokStore.error.adAccounts);
  const adCampaignsError = computed(() => tiktokStore.error.adCampaigns);

  async function getAdAccounts({ brandIds }) {
    await tiktokStore.getAdAccounts({ brandIds: unref(brandIds) });
  }

  async function getAdCampaigns({ brandIds, adAccountIds, sourceCampaignIds }, axiosConfig) {
    await tiktokStore.getAdCampaigns(
      {
        sourceAdAccountIds: unref(adAccountIds),
        brandIds: unref(brandIds),
        sourceCampaignIds: unref(sourceCampaignIds),
      },
      axiosConfig,
    );
  }

  function brandHasPermission(brand) {
    return identityStore.guard(BRAND.ADS.CAN_ACCESS_TIKTOK_ADS, brand);
  }

  function userHasPermission(brand) {
    return identityStore.guard(USER.ADS.CAN_ACCESS_TIKTOK_ADS, brand);
  }

  function userAndBrandHavePermission(brand) {
    return brandHasPermission(brand) && userHasPermission(brand);
  }

  return {
    adAccountsList,
    adAccountsLoading,
    adCampaigns,
    adCampaignsList,
    adCampaignsLoading,
    adAccountToCurrency,
    adAccountsError,
    adCampaignsError,
    getAdAccounts,
    getAdCampaigns,
    brandHasPermission,
    userHasPermission,
    userAndBrandHavePermission,
  };
}
